<template>
  <teleport to="body">
    <div class="modal">
      <div class="modal__layout">
        <h1 class="modal__title typo-ignore typo-title">
          <span>{{$t('shafts.issues.select_member')}}</span>
          <button class="modal__close" @click="cancel">
            <UseSvg name="close_16" class="icon icon--small" />
          </button>
        </h1>

        <div class="modal__content">
          <div class="form__group">
            <Loader v-if="loadingElementTypes" />
            <FormRow
              v-else
              :label="$t('shafts.issues.element_type')"
              labelFor="member_element_type"
              :modifiers="[
                'margin',
                'narrow',
                'lean',
                'lateral',
              ]"
            >
              <div class="form-row__input">
                <Select
                  v-model="elementType"
                  :placeholder="$t('tasks.unspecified')"
                  :groups="elementTypeOptions"
                />
              </div>
            </FormRow>
          </div>

          <Loader v-if="loadingElements" />
          <template v-else>
            <template v-if="locationType === 'shaft'">
              <div class="form__group">
                <FormRow
                  :label="$t('shafts.issues.element')"
                  labelFor="member_element"
                  :modifiers="[
                    'margin',
                    'narrow',
                    'lean',
                    'lateral',
                  ]"
                >
                  <div class="form-row__input">
                    <Select
                      v-model="element"
                      :placeholder="$t('tasks.unspecified')"
                      :groups="elementOptions"
                    />
                  </div>
                </FormRow>
              </div>

              <div class="form__group" v-if="element">
                <FormRow
                  :label="$t('shafts.issues.set')"
                  labelFor="member_set"
                  :modifiers="[
                    'margin',
                    'narrow',
                    'lean',
                    'lateral',
                  ]"
                >
                  <div class="form-row__input">
                    <div class="input input--short input--right">
                      <input id="member_set" type="text" :placeholder="$t('shafts.issues.set_placeholder')" v-model="set" />
                    </div>
                    <div class="form-row__after-input typo-tiny">
                      <div>
                        {{ $t('shafts.issues.set_min') }} = {{ element.start_set }}
                      </div>
                      <div>
                        {{ $t('shafts.issues.set_max') }} = {{ element.end_set }}
                      </div>
                    </div>
                  </div>
                </FormRow>
              </div>
            </template>
            <template v-else-if="locationType === 'station'">
              <div class="form__group">
                <FormRow
                  :label="$t('shafts.issues.station')"
                  labelFor="member_station"
                  :modifiers="[
                    'margin',
                    'narrow',
                    'lean',
                    'lateral',
                  ]"
                >
                  <div class="form-row__input">
                    <Select
                      v-model="station"
                      :placeholder="$t('tasks.unspecified')"
                      :items="stationOptions"
                    />
                  </div>
                </FormRow>
              </div>

              <div class="form__group" v-if="station">
                <FormRow
                  :label="$t('shafts.issues.member')"
                  labelFor="member_station_member"
                  :modifiers="[
                    'margin',
                    'narrow',
                    'lean',
                    'lateral',
                  ]"
                >
                  <div class="form-row__input">
                    <Select
                      v-model="stationMember"
                      :placeholder="$t('tasks.unspecified')"
                      :items="stationMemberOptions"
                    />
                  </div>
                </FormRow>
              </div>
            </template>
          </template>
        </div>

        <div class="modal__actions">
          <button class="button" @click="cancel">{{ $t('general.cancel') }}</button>
          <FormSubmitButton @click="save" buttonLabel="Ok" :isSaving="saving" :disabled="!valid" />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';
import { v4 as uuid } from 'uuid';

import UseSvg from '@/components/generic/use_svg.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import DatePicker from '@/components/form/date_picker.vue';
import LabelInput from '@/components/form/label_input.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import FileIcon from '@/components/generic/file_icon.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import Loader from '@/components/generic/loader.vue';

import useAjax from '@/functions/use_ajax';
import pluralize from '@/functions/pluralize';

const props = defineProps({
  location: { type: Object, default: null },
});
const emit = defineEmits(['cancel', 'submit']);

const route = useRoute();
const ajax = useAjax();

const elementsPath = computed(() => `/organisations/${route.params.organisationId}/shafts/${route.params.shaftId}/elements`)

const saving = ref(false);
const loadingElementTypes = ref(true);
const loadingElements = ref(false);

const elementTypes = ref(null);
const elements = ref(null);
const stations = ref(null);

const element = ref(props.location?.element);
const set = ref(props.location?.set);

const station = ref(props.location?.station);
const stationMember = ref(props.location?.stationMember);

const elementType = ref(props.location ? `${props.location.type}/${props.location.elementType}` : null);

const locationType = computed(() => {
  if (!elementType.value) return null;

  return elementType.value.split('/')[0];
});

const baseElementType = computed(() => {
  if (!elementType.value) return null;

  return elementType.value.split('/')[1];
});

const elementTypeOptions = computed(() => {
  if (loadingElementTypes.value) return [];

  return [
    {
      label: I18n.t('shafts.issues.shaft_elements'),
      items: elementTypes.value.shaft_elements.map(element => ({
        label: I18n.t(`shafts.element_types.shaft.${element}`), value: `shaft/${element}`
      }))
    },
    {
      label: I18n.t('shafts.issues.station_elements'),
      items: elementTypes.value.station_template_member_types.map(element => ({
        label: I18n.t(`shafts.element_types.station.${element}`), value: `station/${element}`
      }))
    }
  ];
});

const elementOptions = computed(() => {
  if (loadingElementTypes.value || loadingElements.value) return [];
  if (locationType.value !== 'shaft') return [];

  return Object.entries(elements.value).map(([key, value]) => ({
    label: key,
    items: value.map(element => ({ label: element.name, value: element })),
  }));
});

const stationOptions = computed(() => {
  if (loadingElementTypes.value || loadingElements.value) return [];
  if (locationType.value !== 'station') return [];

  return stations.value.map(station => ({ label: station.name, value: station }));
});

const stationMemberOptions = computed(() => {
  if (loadingElementTypes.value || loadingElements.value) return [];
  if (locationType.value !== 'station') return [];
  if (!station.value) return [];

  return station.value.station_template.station_template_members
    .map(member => ({ label: member.name, value: member }))
});

const location = computed(() => {
  switch (locationType.value) {
    case 'shaft':
      return {
        type: 'shaft',
        elementType: baseElementType.value,
        element: element.value,
        set: parseInt(set.value, 10),
      };
    case 'station':
      return {
        type: 'station',
        elementType: baseElementType.value,
        station: station.value,
        stationMember: stationMember.value
      };
  }
  return {};
});

const valid = computed(() => {
    switch (locationType.value) {
      case 'shaft': {
        if (!element.value || !set.value) return false;
        let setNumber = parseInt(set.value, 10);
        if (setNumber < element.value.start_set || setNumber > element.value.end_set) return false;
        return true;
      }
      case 'station':
        return station.value && stationMember.value;
      default: return false;
    }
});

onMounted(() => {
  fetchElementTypes();
  if (elementType.value) {
    fetchElements();
  }
});

watch(elementType, (newValue, oldValue) => {
  element.value = null;
  set.value = null;
  station.value = null;
  stationMember.value = null;
  fetchElements();
});

async function fetchElementTypes() {
  loadingElementTypes.value = true;
  let { ok, data } = await ajax(elementsPath.value);

  if (ok) {
    elementTypes.value = data;
  } else {
    elementTypes.value = { shaft_elements: [], station_template_member_types: [] };
  }
  loadingElementTypes.value = false;
}

async function fetchElements() {
  loadingElements.value = true;
  let { ok, data } = await ajax(`${elementsPath.value}/${baseElementType.value}`);

  if (ok) {
    elements.value = data.shaft_elements || {};
    Object.entries(elements.value).forEach(([group, groupElements]) => {
      groupElements.forEach(e => e.group_name = group);
    });
    stations.value = data.station_template_member_types || [];

    if (element.value) {
      element.value = Object.values(elements.value).flat().find(({ id }) => id === element.value.id);
    }
    if (station.value) {
      station.value = stations.value.find(({ id }) => id === station.value.id);
      if (stationMember.value) {
        stationMember.value = station.value.station_template.station_template_members
          .find(({ id }) => id === stationMember.value.id);
      }
    }
  } else {
    elements.value = {};
    stations.value = [];
  }
  loadingElements.value = false;
}

function cancel() {
  emit('cancel');
}

function isValid() {
  return true;
}

function save() {
  saving.value = true;

  if (isValid()) {
    emit('submit', location.value);
  }

  saving.value = false;
}
</script>
